import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { createApiContext } from "../../../shared/api/create-api-context";
import type { Exam, ExamsApi, ExamTimelineRow } from "./exams-api";

const context = createApiContext<ExamsApi>("Exams");
export const ExamsApiProvider = context.Provider;
export const useExamsApi = context.useApi;

export const examKeys = {
  all: (profileId: string) => ["exams", profileId] as const,
  list: (profileId: string, page: number) => [...examKeys.all(profileId), "list", page] as const,
  timeline: (profileId: string) => [...examKeys.all(profileId), "timeline"] as const,
};

export function useExams(profileId: string, page = 0): UseQueryResult<Exam[]> {
  const api = useExamsApi();
  return useQuery({
    queryKey: examKeys.list(profileId, page),
    queryFn: ({ signal }) => api.list(profileId, page, signal),
    enabled: profileId !== "",
  });
}

export function useExamTimeline(profileId: string): UseQueryResult<ExamTimelineRow[]> {
  const api = useExamsApi();
  return useQuery({
    queryKey: examKeys.timeline(profileId),
    queryFn: ({ signal }) => api.listForTimeline(profileId, signal),
    enabled: profileId !== "",
  });
}

export function useDeleteExam(profileId: string) {
  const api = useExamsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (exam: Exam) => api.deleteExam(exam),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: examKeys.all(profileId) });
    },
  });
}

export function useExamFileUrl() {
  const api = useExamsApi();
  return useMutation({
    mutationFn: (input: { exam: Exam; seconds: number | null }) =>
      api.fileUrl(input.exam, input.seconds),
    retry: false,
  });
}
